<template>
  <section class="mt-section">
    <div class="section-container">
      <div class="section-label">تقييمك</div>
      <h2 class="section-title">شاركنا رأيك ✍️</h2>
      <p class="section-sub">تقييمك يساعد زملاءك في اتخاذ القرار</p>
    </div>

    <div class="mt-card-wrap">
      <div class="mt-card">

        <!-- Stars picker -->
        <div class="mt-stars-row">
          <span class="mt-stars-label">تقييمك بالنجوم</span>
          <StarPicker v-model="stars" />
          <span class="mt-stars-count" v-if="stars">{{ stars }} / 5</span>
        </div>

        <!-- Textarea + emoji toggle -->
        <div class="mt-textarea-wrap">
          <textarea
            ref="textareaRef"
            v-model="note"
            class="mt-textarea"
            placeholder="اكتب رأيك في منصة أ/ طارق..."
            rows="4"
            maxlength="300"
          ></textarea>
          <div class="mt-textarea-tools">
            <span class="mt-char-count">{{ note.length }} / 300</span>
            <EmojiPickerBtn @emoji-select="appendEmoji" />
          </div>
        </div>

        <!-- Validation hint -->
        <div class="mt-hint" v-if="!stars || !note.trim()">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="8" cy="8" r="6.5"/>
            <path d="M8 5v4M8 10.5v.5"/>
          </svg>
          {{ !stars ? 'اختر عدد النجوم أولاً' : 'اكتب رأيك قبل الحفظ' }}
        </div>

        <!-- Success toast -->
        <Transition name="mt-fade">
          <div class="mt-success" v-if="saved">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            تم حفظ تقييمك بنجاح! 🎉
          </div>
        </Transition>

        <!-- Save button -->
        <button class="mt-save-btn" @click="handleSave" :disabled="saving || !stars || !note.trim()">
          <span v-if="!saving">{{ myTestimonial ? '💾 تحديث التقييم' : '🚀 نشر التقييم' }}</span>
          <span v-else class="mt-spinner"></span>
        </button>

        <!-- Existing notice -->
        <p class="mt-existing" v-if="myTestimonial">
          لديك تقييم سابق — سيتم تحديثه عند الحفظ
        </p>

      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue';
import StarPicker from '@/components/common/StarPicker.vue';
import EmojiPickerBtn from '@/components/common/EmojiPickerBtn.vue';
import { testimonialsService } from '@/services/testimonialsService';

const props = defineProps({
  myTestimonial: { type: Object, default: null },
});
const emit = defineEmits(['saved']);

const note    = ref('');
const stars   = ref(0);
const saving  = ref(false);
const saved   = ref(false);
const textareaRef = ref(null);

// Pre-fill when parent passes existing testimonial
watch(() => props.myTestimonial, val => {
  if (val) {
    note.value  = val.note;
    stars.value = val.number_of_stars;
  }
}, { immediate: true });

function appendEmoji(emoji) {
  const el = textareaRef.value;
  if (!el) { note.value += emoji; return; }

  const start = el.selectionStart ?? note.value.length;
  const end   = el.selectionEnd   ?? note.value.length;
  note.value  = note.value.slice(0, start) + emoji + note.value.slice(end);

  // Restore cursor after emoji
  setTimeout(() => {
    el.focus();
    const pos = start + emoji.length;
    el.setSelectionRange(pos, pos);
  }, 0);
}

async function handleSave() {
  if (!stars.value || !note.value.trim()) return;
  saving.value = true;
  saved.value  = false;
  try {
    const result = await testimonialsService.save(note.value.trim(), stars.value);
    if (result.status) {
      saved.value = true;
      setTimeout(() => { saved.value = false; }, 3000);
      emit('saved', result.data);
    }
  } catch {
    // silent
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.section-container { max-width:1200px;margin:0 auto;padding:0 2rem; }
.section-label { display:inline-block;background:rgba(91,95,239,.08);color:#5B5FEF;border:1px solid rgba(91,95,239,.2);border-radius:99px;padding:.3rem 1rem;font-size:.8rem;font-weight:700;margin-bottom:1rem; }
.section-title { font-size:clamp(1.6rem,3vw,2.2rem);font-weight:800;color:#111827;margin-bottom:0;line-height:1.3; }
.section-sub { font-size:.92rem;color:#6B7280;margin-top:.4rem;text-align:center; }

.mt-section { padding:5rem 0 4rem;background:linear-gradient(180deg,#F5F7FB 0%,#EEF2FF 100%);font-family:'Cairo',sans-serif;direction:rtl; }
.mt-card-wrap { max-width:640px;margin:2.5rem auto 0;padding:0 1.25rem; }
.mt-card { background:rgba(255,255,255,.95);backdrop-filter:blur(20px);border-radius:28px;padding:2.25rem 2rem;box-shadow:0 20px 60px rgba(91,95,239,.1),0 4px 16px rgba(0,0,0,.06);border:1px solid rgba(255,255,255,.9);display:flex;flex-direction:column;gap:1.25rem; }

/* Stars row */
.mt-stars-row { display:flex;align-items:center;gap:1rem;flex-wrap:wrap; }
.mt-stars-label { font-size:.85rem;font-weight:700;color:#374151; }
.mt-stars-count { font-size:.8rem;font-weight:700;color:#5B5FEF; }

/* Textarea area */
.mt-textarea-wrap { position:relative; }
.mt-textarea { width:100%;padding:.9rem 1rem 2.5rem;border:2px solid #E5E7EB;border-radius:14px;font-family:'Cairo',sans-serif;font-size:.95rem;color:#1A1A1A;background:#F9FAFB;resize:vertical;min-height:110px;outline:none;transition:border-color .25s,box-shadow .25s,background .25s;direction:rtl; }
.mt-textarea:focus { border-color:#5B5FEF;background:#fff;box-shadow:0 0 0 4px rgba(91,95,239,.1); }
.mt-textarea-tools { position:absolute;left:.75rem;bottom:.6rem;display:flex;align-items:center;gap:.6rem; }
.mt-char-count { font-size:.72rem;color:#9CA3AF; }

/* Hint */
.mt-hint { display:flex;align-items:center;gap:.5rem;font-size:.8rem;color:#9CA3AF;font-weight:600; }
.mt-hint svg { width:15px;height:15px;flex-shrink:0; }

/* Success toast */
.mt-success { display:flex;align-items:center;gap:.6rem;background:rgba(34,197,94,.08);border:1.5px solid rgba(34,197,94,.2);border-radius:12px;padding:.75rem 1rem;color:#16a34a;font-weight:700;font-size:.9rem; }
.mt-success svg { width:20px;height:20px;flex-shrink:0;fill:#16a34a; }
.mt-fade-enter-active, .mt-fade-leave-active { transition:all .4s cubic-bezier(.4,0,.2,1); }
.mt-fade-enter-from, .mt-fade-leave-to { opacity:0;transform:translateY(8px); }

/* Save button */
.mt-save-btn { padding:.9rem 2rem;border:none;border-radius:14px;background:linear-gradient(135deg,#5B5FEF,#7C4DFF);color:#fff;font-family:'Cairo',sans-serif;font-size:1rem;font-weight:800;cursor:pointer;box-shadow:0 12px 30px rgba(91,95,239,.3);transition:all .3s;display:flex;align-items:center;justify-content:center;min-height:52px; }
.mt-save-btn:hover:not(:disabled) { transform:translateY(-3px);box-shadow:0 20px 45px rgba(91,95,239,.45); }
.mt-save-btn:disabled { opacity:.6;cursor:not-allowed; }

/* Spinner */
.mt-spinner { width:22px;height:22px;border:3px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:mtSpin .8s linear infinite;display:inline-block; }
@keyframes mtSpin { to { transform:rotate(360deg); } }

.mt-existing { font-size:.78rem;color:#9CA3AF;text-align:center; }
</style>
